// Part libraries: the actual shapes of each mech's body parts, authored in the
// part's local frame in the neutral upright pose (see rig.ts conventions).
//
//   legs:  origin at the joint, +y runs down the bone, +x is forward
//   torso: origin at the pelvis centre, −y runs up the spine
//   head:  origin at the neck pivot
//   foot:  origin at the ankle, flat sole at y = ankleH, toe at +toe, heel at −heel
//
// Arms are NOT part of the body (they are separate runtime parts, see arms.ts).

import { cutoutFor, equipOverride } from './cutout';
import type { Drawer } from './draw';
import type { FrameFx } from './poses';
import { apply, type Skeleton } from './rig';
import type { MechStyle, Pt } from './styles';
import type { MechId } from '../../core/types';

export type Side = 'F' | 'R';

export interface PartLib {
  thigh(d: Drawer, fx: FrameFx, side: Side): void;
  shin(d: Drawer, fx: FrameFx, side: Side): void;
  foot(d: Drawer, fx: FrameFx, side: Side): void;
  /** Hip block, drawn behind the torso. */
  pelvis(d: Drawer, fx: FrameFx): void;
  /** Front hip armour, drawn over the top of the front thigh. */
  hipPlate(d: Drawer, fx: FrameFx): void;
  /** Drawn before the torso. */
  backpack(d: Drawer, fx: FrameFx): void;
  torso(d: Drawer, fx: FrameFx): void;
  head(d: Drawer, fx: FrameFx): void;
  /** Cell-space extras behind everything (floating rear parts). */
  behind?(d: Drawer, style: MechStyle, sk: Skeleton, fx: FrameFx): void;
  /** Cell-space extras between the torso and the head (halo). */
  behindHead?(d: Drawer, style: MechStyle, sk: Skeleton, fx: FrameFx): void;
  /** Cell-space extras in front of the body (floating front parts). */
  front?(d: Drawer, style: MechStyle, sk: Skeleton, fx: FrameFx): void;
  /** True to draw the head before (under) the torso (a neck that tucks into the collar). */
  headBehind?(): boolean;
}

// ───────────────────────────── VOLT ─────────────────────────────
// Slim racing frame: angular light-steel plates, cobalt trim, swept crest,
// visor slit, cyan reactor in the chest prow, light strips on thighs/shins.

const VOLT_VECTOR: PartLib = {
  thigh(d) {
    d.plate([[-3.5, -1], [3, -1], [3.5, 20], [-3, 20.5]], 'frame', { sh: 1.5 });
    d.plate([[-6, -3], [4.5, -3.5], [7, 4], [6, 12.5], [3, 18.5], [-3.5, 17.5], [-6.5, 8]], 'armor', { sh: 3, hl: 1.2, r: 0.7 });
    d.edge([[-5.6, -2.6], [4.2, -3.1]]);
    d.panel([[-6.5, 9.5], [6, 9]]);
    d.plate([[2.2, 0.5], [6.2, 2.2], [5.8, 8.6], [2.2, 8.2]], 'accent', { sh: 1, hl: 0.8, ink: 0.6 });
    d.strip([[5.8, 11], [3.6, 16.5]], 1.1);
  },
  shin(d, fx, side) {
    void fx;
    void side;
    d.plate([[-4, 1], [-8.5, 6], [-7.5, 14.5], [-4, 17.5]], 'frame', { sh: 1.5 });
    d.disc(0, 0, 3.6, 'metal');
    d.plate([[-5, -1.5], [5, -2.5], [6.2, 5], [4.2, 21], [-2.5, 22], [-5.5, 11]], 'armor', { sh: 3, hl: 1.2, r: 0.7 });
    d.panel([[-5.5, 11.5], [5.5, 10.5]]);
    d.edge([[-4.6, -1.3], [4.6, -2.2]]);
    d.plate([[-3.5, 18], [4.5, 17.5], [4.3, 22.6], [-3, 23]], 'frame', { sh: 1, hl: 0.6 });
    d.plate([[-1.5, -4.5], [5.5, -6.2], [9.6, -1.6], [6.6, 3.6], [0.5, 3]], 'accent', { sh: 1.6, hl: 1 });
    d.edge([[-1.2, -4.4], [5.4, -6]]);
    d.strip([[5.5, 5.5], [3.8, 15.5]], 1.2);
  },
  foot(d) {
    d.disc(0, 0, 3, 'metal');
    d.plate([[-6, 0.5], [-3, -2.6], [4, -2.2], [9, 1.4], [12, 3.2], [12, 5], [-6, 5]], 'armor', { sh: 2, hl: 1, r: 0.6 });
    d.edge([[-3, -2.4], [3.8, -2]]);
    d.plate([[6.4, 0.6], [10.4, 2.5], [12, 3.8], [12, 5], [6.4, 5]], 'accent', { sh: 1, hl: 0.7, ink: 0.6 });
    d.plate([[-6, 3.6], [12, 3.6], [12, 5], [-6, 5]], 'frame', { sh: 0, hl: 0, ink: 0.5 });
    d.plate([[-4.5, -0.5], [-7.5, 4.6], [-4, 4.6]], 'frame', { sh: 0.8, hl: 0.5, ink: 0.6 });
  },
  pelvis(d) {
    d.plate([[-7, -4], [6, -4], [7, 3], [3, 7.5], [-5, 7.5], [-8, 2]], 'frame', { sh: 2 });
    d.plate([[-10.5, -3.5], [-5, -4.5], [-4, 4], [-7, 8], [-10.5, 4.5]], 'plate', { sh: 1.8, hl: 0.9 });
  },
  hipPlate(d) {
    d.plate([[-3.5, -4.5], [7, -5], [10, 0.5], [7.5, 7.5], [1.5, 6.5], [-2.5, 2]], 'armor', { sh: 2.5, hl: 1.1 });
    d.edge([[-3, -4.3], [6.8, -4.8]]);
    d.plate([[2.5, -3.4], [7.3, -3.7], [8.7, -0.4], [4.4, 0.6]], 'accent', { sh: 0.8, hl: 0.6, ink: 0.6 });
  },
  backpack(d) {
    d.plate([[-14, -34], [-21, -41], [-20, -35.5], [-17, -30.5]], 'accent', { sh: 1.2, hl: 0.8 });
    d.plate([[-9.5, -17], [-15.5, -19], [-17.5, -30], [-15, -37.5], [-9, -37]], 'plate', { sh: 2.5, hl: 1 });
    d.panel([[-16.8, -26], [-10, -26]]);
    d.strip([[-15.6, -21.5], [-16.8, -28.5]], 1.1, 0.9);
  },
  torso(d) {
    // Waist: dark segmented spine.
    d.plate([[-5, -1], [4.5, -1], [5.5, -16], [-5.5, -16]], 'frame', { sh: 2, hl: 0.8 });
    d.panel([[-5.2, -5.5], [5, -5.5]]);
    d.panel([[-5.4, -10], [5.3, -10]]);
    // Chest block.
    d.plate([[-10, -15], [6.5, -16], [11.5, -22], [11.5, -30], [7, -36.5], [-1, -39.5], [-8, -38.5], [-11, -31]], 'armor', { sh: 4.5, hl: 1.3, r: 0.8 });
    d.edge([[-7.5, -38.3], [-1, -39.2], [6.6, -36.4]]);
    d.panel([[-10.5, -24], [-3, -24.5], [-1, -27.5]]);
    // Shoulder-line trim.
    d.plate([[-8.5, -36], [-1, -38.5], [6.5, -35.5], [5, -33.2], [-1, -35.4], [-8.8, -33.4]], 'accent', { sh: 0.8, hl: 0.7, ink: 0.6 });
    // Lower chest plate.
    d.plate([[-8, -15], [6.5, -16], [7.5, -19.5], [-7.5, -20]], 'plate', { sh: 1.2, hl: 0.6, ink: 0.7 });
    d.strip([[-6.8, -21.6], [-1.5, -21.8]], 1);
    // Chest prow with the reactor.
    d.plate([[3.5, -16.5], [11.8, -21.5], [11.8, -29.5], [7, -31.5], [2.5, -26.5]], 'armor', { sh: 2, hl: 1, ink: 0.7 });
    d.disc(7.8, -23.5, 4.5, 'frame', { sh: 1.2, hl: 0.8 });
    d.lampDisc(7.8, -23.5, 3, 1);
  },
  head(d) {
    d.plate([[-2.5, 1.5], [2.5, 1.5], [2.2, -4], [-2.2, -4]], 'frame', { sh: 1 });
    d.plate([[-2, -13.5], [-12.5, -18.2], [-9.5, -13], [-6.5, -9.5]], 'accent', { sh: 1.2, hl: 0.8 });
    d.plate([[-6.5, -3], [-7, -10], [-3.5, -14.5], [3.5, -14.5], [8.5, -10], [9.5, -5.5], [5.5, -2], [-2.5, -1.5]], 'armor', { sh: 2.6, hl: 1.2, r: 0.8 });
    d.edge([[-6.6, -9.8], [-3.4, -14.2], [3.4, -14.2]]);
    d.plate([[-4, -8], [0.5, -8.6], [1, -4.5], [-3.2, -3.6]], 'plate', { sh: 0.8, hl: 0.5, ink: 0.6 });
    d.plate([[2.5, -10.5], [8.8, -9], [9.6, -5], [5.5, -2.8], [2.5, -6.5]], 'frame', { sh: 0.8, hl: 0.6, ink: 0.7 });
    d.lampPoly([[3.4, -8.7], [9.4, -7.7], [9.4, -6.5], [3.6, -7.1]], 1, 0.4);
  },
  front(d, style, sk, fx) {
    if (fx.arcs > 0.01) voltArcs(d, style, sk, fx);
  },
};

type CutoutExtras = Pick<PartLib, 'behind' | 'behindHead' | 'front'>;

/**
 * A mech drawn with its painted cutout parts (public/assets/mech/<id>, see cutout.ts); every
 * part falls back to the vector library when the paintings did not load. Frames follow
 * rig.ts: legs extend along +y from their joint, the torso and pelvis share the pelvis-centre
 * origin, the head hangs from the neck pivot. `extras` replaces the vector library's cell-space
 * hooks while painted (effects are kept; vector armour the painting already contains is not).
 */
function withCutout(id: MechId, vector: PartLib, extras: CutoutExtras = {}): PartLib {
  const c = () => cutoutFor(id);
  const hook =
    (k: keyof CutoutExtras) =>
    (d: Drawer, style: MechStyle, sk: Skeleton, fx: FrameFx): void =>
      (c() ? extras[k] : vector[k])?.(d, style, sk, fx);
  return {
    thigh(d, fx, side) {
      const k = c();
      if (!k) return vector.thigh(d, fx, side);
      d.image(k.parts.thigh, 'hip', 0, 0, k.layout.thighRot);
    },
    shin(d, fx, side) {
      const k = c();
      if (!k) return vector.shin(d, fx, side);
      const o = equipOverride(id, 'shin');
      if (o) d.image(o.img, 'knee', 0, 0, o.rot);
      else d.image(k.parts.shin, 'knee', 0, 0, k.layout.shinRot);
    },
    foot(d, fx, side) {
      const k = c();
      if (!k) return vector.foot(d, fx, side);
      d.image(k.parts.foot, 'ankle');
    },
    pelvis(d, fx) {
      const k = c();
      if (!k) return vector.pelvis(d, fx);
      d.image(k.parts.pelvis, 'hip', k.layout.hip[0], k.layout.hip[1]);
    },
    hipPlate(d, fx) {
      const k = c();
      if (!k) return vector.hipPlate(d, fx);
      const hp = k.layout.hipPlate;
      d.image(k.parts.hipplate, 'mount', hp.at[0], hp.at[1], hp.rot);
    },
    backpack(d, fx) {
      const k = c();
      if (!k) return vector.backpack(d, fx);
      const f = k.layout.fins;
      const o = equipOverride(id, 'fins');
      if (o) d.image(o.img, 'mount', f.at[0], f.at[1], f.rot + o.rot);
      else d.image(k.parts.fins, 'mount', f.at[0], f.at[1], f.rot);
    },
    torso(d, fx) {
      const k = c();
      if (!k) return vector.torso(d, fx);
      const o = equipOverride(id, 'torso');
      if (o) d.image(o.img, 'waist', k.layout.waist[0], k.layout.waist[1], o.rot);
      else d.image(k.parts.torso, 'waist', k.layout.waist[0], k.layout.waist[1]);
    },
    head(d, fx) {
      const k = c();
      if (!k) return vector.head(d, fx);
      d.image(k.parts.head, 'neck');
    },
    behind: hook('behind'),
    behindHead: hook('behindHead'),
    front: hook('front'),
    headBehind: () => !!c()?.parts.head.behind,
  };
}

/** VOLT keeps its Arc Dash discharge over the painted parts. */
const VOLT = withCutout('volt', VOLT_VECTOR, { front: VOLT_VECTOR.front });

/** Static discharge crawling over VOLT during Arc Dash (both passes; tinted by the reactor in the glow sheet). */
function voltArcs(d: Drawer, style: MechStyle, sk: Skeleton, fx: FrameFx): void {
  const rnd = seeded(fx.seed);
  const pairs: [Pt, Pt][] = [
    [sk.legF.knee, sk.legF.ankle],
    [apply(sk.torso, [9, -22]), apply(sk.torso, [2, -4])],
    [apply(sk.torso, [-15, -34]), apply(sk.torso, [-22, -46])],
    [sk.legR.knee, apply(sk.legR.foot, [-6, 5])],
    [apply(sk.head, [-9, -15]), apply(sk.head, [-16, -19])],
  ];
  const n = Math.round(2 + fx.arcs * 3);
  for (let i = 0; i < n; i++) {
    const [a, b] = pairs[(i + Math.floor(rnd() * 5)) % pairs.length];
    d.energy(jagged(a, b, 5, 2.4, rnd), 0.9, 0.55 + 0.45 * fx.arcs);
  }
  void style;
}

// ───────────────────────────── BASTION ─────────────────────────────
// Heavy siege frame: torso block with a collar rising behind a sunken head,
// pillar legs with big knee caps, slab feet, hazard chevrons, amber reactor port.

const BASTION_VECTOR: PartLib = {
  thigh(d) {
    d.plate([[-5, -1], [5, -1], [5.5, 18], [-5, 18.5]], 'frame', { sh: 2 });
    d.plate([[-8.5, -3], [7.5, -3.5], [9.2, 6], [8.2, 15], [5, 19], [-5.5, 19], [-9.2, 9]], 'armor', { sh: 3.6, hl: 1.2, r: 0.9 });
    d.edge([[-8, -2.6], [7.2, -3.1]]);
    d.plate([[1.2, 1.8], [8.6, 2.6], [8.4, 8.4], [1.2, 8]], 'accent', { sh: 1.2, hl: 0.8, ink: 0.6 });
    d.panel([[-9, 11], [8.6, 11]]);
    d.bolt(-6, 1.5);
    d.bolt(-6, 14);
  },
  shin(d, fx) {
    d.plate([[-6, 1], [-11, 4.5], [-10.5, 15], [-6.5, 19]], 'frame', { sh: 1.8 });
    d.disc(0, 0, 5, 'metal');
    d.plate([[-8, -2], [8, -3], [9.6, 6], [8.6, 19], [-6.5, 20], [-9.2, 10]], 'armor', { sh: 3.8, hl: 1.2, r: 0.9 });
    d.edge([[-7.5, -1.8], [7.6, -2.8]]);
    // Hazard chevrons.
    d.plate([[1.5, 7], [8.9, 8.6], [8.8, 11], [1.5, 9.6]], 'accent', { sh: 0.6, hl: 0.5, ink: 0.5 });
    d.plate([[1.5, 12], [8.8, 13.6], [8.7, 16], [1.5, 14.6]], 'accent', { sh: 0.6, hl: 0.5, ink: 0.5 });
    d.plate([[-7, 16.5], [8.5, 15.8], [8.6, 20.3], [-6.5, 21]], 'frame', { sh: 1, hl: 0.6 });
    d.plate([[-3, -6], [6, -7.2], [10.4, -2.4], [8.6, 4.2], [0, 3.6]], 'accent', { sh: 2, hl: 1.1 });
    d.edge([[-2.6, -5.9], [5.8, -7]]);
    d.bolt(6.4, -0.8);
    d.strip([[-5.2, 4], [-5.8, 13]], 1.1, 0.7);
    // Siege anchor strut (signature): swings down behind the heel to brace on the ground.
    if (fx.anchor > 0.01) {
      const k = fx.anchor;
      d.push(-6, 15, 0.9 - 0.55 * k);
      d.plate([[-1.8, 0], [1.8, 0], [1.4, 9 + 6 * k], [0, 11 + 7 * k], [-1.4, 9 + 6 * k]], 'metal', { sh: 0.8, hl: 0.6 });
      d.pop();
    }
  },
  foot(d) {
    d.disc(0, 0, 4.2, 'metal');
    d.plate([[-10, 1], [-7, -3.2], [6, -3.2], [12, 1], [14, 4], [14, 7], [-10, 7]], 'armor', { sh: 2.6, hl: 1.1, r: 0.8 });
    d.edge([[-7, -3], [5.8, -3]]);
    d.plate([[6.2, -0.6], [12, 1.4], [14, 4.2], [14, 7], [6.2, 7]], 'plate', { sh: 1.2, hl: 0.8, ink: 0.6 });
    d.plate([[-10, 4.8], [14, 4.8], [14, 7], [-10, 7]], 'frame', { sh: 0, hl: 0, ink: 0.5 });
    d.bolt(-6.5, 1.5);
    d.bolt(9, 2.6);
  },
  pelvis(d) {
    d.plate([[-11, -5], [10, -5], [11, 4], [6, 9.5], [-7, 9.5], [-12, 4]], 'frame', { sh: 2.4 });
    d.plate([[-15.5, -4], [-8, -5], [-7, 6], [-11, 11.5], [-16, 6]], 'plate', { sh: 2, hl: 1 });
    d.bolt(-12, 0);
  },
  hipPlate(d) {
    d.plate([[-4, -6], [11, -6], [14.5, 1], [11.5, 11], [2, 10], [-3, 3]], 'armor', { sh: 3, hl: 1.2, r: 0.8 });
    d.edge([[-3.5, -5.8], [10.8, -5.8]]);
    d.plate([[3, -4.2], [10.6, -4.2], [12.2, -0.4], [4, -0.4]], 'accent', { sh: 0.8, hl: 0.6, ink: 0.6 });
    d.bolt(1, 3);
    d.bolt(9, 6);
  },
  backpack(d, fx) {
    d.plate([[-16, -12], [-25.5, -14], [-27.5, -34], [-23.5, -40.5], [-15, -38.5]], 'plate', { sh: 3, hl: 1.1, r: 0.8 });
    d.panel([[-27, -20], [-17, -20]]);
    d.panel([[-27.2, -25.5], [-17, -25.5]]);
    d.panel([[-27.3, -31], [-17, -31]]);
    d.plate([[-23, -41.5], [-19, -47], [-16.5, -46], [-17.5, -39.5]], 'frame', { sh: 1, hl: 0.6 });
    d.strip([[-26, -16.5], [-26.6, -22]], 1.2, 0.8 + 0.6 * fx.flare);
    d.strip([[-26.8, -28], [-27, -33]], 1.2, 0.8 + 0.6 * fx.flare);
  },
  torso(d, fx) {
    d.plate([[-9.5, -1], [8.5, -1], [9.5, -13], [-10.5, -13]], 'frame', { sh: 2.4 });
    d.panel([[-10, -5], [9, -5]]);
    d.panel([[-10.2, -9], [9.2, -9]]);
    // Collar rising behind the head.
    d.plate([[-13, -36], [-4, -38], [-2.5, -45.5], [-9.5, -47], [-15, -41]], 'armor', { sh: 2.4, hl: 1.1 });
    d.edge([[-14.4, -41], [-9.5, -46.6], [-3, -45.3]]);
    // Torso block.
    d.plate([[-17.5, -11], [13, -12], [17, -17], [17.5, -32], [12, -38.5], [-12, -39.5], [-18.5, -33]], 'armor', { sh: 5.5, hl: 1.4, r: 1.1 });
    d.edge([[-17.6, -33.4], [-12, -39.2], [11.8, -38.2]]);
    d.panel([[-18, -25], [-4, -25.5]]);
    d.panel([[-4, -25.5], [-4, -12.5]]);
    d.bolt(-15, -14.5);
    d.bolt(-15, -35.5);
    d.bolt(-6.5, -14.5);
    // Shoulder hazard band.
    d.plate([[-13.5, -36.8], [10.5, -36.3], [12.8, -33.8], [-15, -34.2]], 'accent', { sh: 0.8, hl: 0.7, ink: 0.6 });
    // Front chest slab housing the reactor port.
    d.plate([[7, -12.5], [17, -17], [17.5, -31.5], [11.5, -35], [6.5, -24]], 'plate', { sh: 2, hl: 1, ink: 0.8 });
    d.disc(12, -23, 6.3, 'frame', { sh: 1.6, hl: 1 });
    d.lampDisc(12, -23, 4.4, 1 + 0.3 * fx.flare);
    d.bolt(12, -30.4, 0.7);
    d.bolt(12, -15.6, 0.7);
    // Abdominal vents.
    d.strip([[-8.5, -16], [-1.5, -16.2]], 1.1, 0.85);
    d.strip([[-8.6, -19], [-1.5, -19.2]], 1.1, 0.7 + 0.5 * fx.flare);
  },
  head(d) {
    d.plate([[-3, 1.5], [3, 1.5], [3, -3], [-3, -3]], 'frame', { sh: 1 });
    d.plate([[-5, -9.5], [-9, -16.5], [-7.5, -17], [-3.5, -10.5]], 'frame', { sh: 0.5, hl: 0.5, ink: 0.6 });
    d.plate([[-6, -1], [-6.5, -8], [-3, -11], [5, -11], [8.6, -7.2], [8.6, -1.8], [5, 0.6], [-4, 0.6]], 'armor', { sh: 2.4, hl: 1.1, r: 0.9 });
    d.edge([[-6.4, -8], [-3, -10.8], [4.8, -10.8]]);
    d.plate([[-3, -11.2], [5.5, -11.2], [8.9, -7.6], [2, -8.2]], 'accent', { sh: 0.8, hl: 0.6, ink: 0.6 });
    d.plate([[2.4, -7.4], [8.8, -6.6], [8.8, -3.6], [2.4, -3.8]], 'frame', { sh: 0.5, hl: 0.4, ink: 0.6 });
    d.lampPoly([[3.2, -6.4], [8.9, -5.9], [8.9, -4.6], [3.2, -4.9]], 1, 0.3);
    d.lampDisc(-8.2, -16.6, 0.9, 0.8, 0.3);
  },
};

// ───────────────────────────── WRAITH ─────────────────────────────
// Narrow frame on long stiletto legs; open chest ring holding a floating
// violet core; detached shoulder fins and a broken halo hover around it.

const WRAITH_VECTOR: PartLib = {
  thigh(d) {
    d.plate([[-2.5, -1], [2.5, -1], [2.8, 23], [-2.2, 23]], 'frame', { sh: 1.2 });
    d.plate([[-4.6, -2.5], [3.6, -3], [5, 6], [4, 16], [2, 22], [-2.5, 21], [-4.8, 10]], 'armor', { sh: 2.6, hl: 1.1, r: 0.6 });
    d.edge([[-4.3, -2.2], [3.4, -2.7]]);
    d.plate([[1.4, 3], [4.6, 4.5], [3.8, 13], [1.2, 12.5]], 'accent', { sh: 0.8, hl: 0.6, ink: 0.5 });
    d.strip([[3.8, 15], [1.9, 20.5]], 1);
  },
  shin(d) {
    d.plate([[-3, 2], [-7.5, 9.5], [-5.8, 20.5], [-3, 23.5]], 'frame', { sh: 1.2 });
    d.disc(0, 0, 3, 'metal');
    d.plate([[-4, -1], [4, -2], [4.6, 7], [2.6, 25], [-2, 25.6], [-4.2, 12]], 'armor', { sh: 2.6, hl: 1.1, r: 0.6 });
    d.edge([[-3.7, -0.8], [3.7, -1.8]]);
    d.panel([[-4.1, 13], [4, 12.2]]);
    d.plate([[-0.5, -3.6], [5, -7.6], [8.6, -6.6], [5.2, 0.6], [0, 2]], 'accent', { sh: 1.2, hl: 0.8 });
    d.strip([[4, 7.5], [2.6, 19]], 1.1);
  },
  foot(d) {
    d.disc(0, 0, 2.6, 'metal');
    d.plate([[-4.5, 0], [-2, -2.4], [3.5, -1.6], [8, 2], [11, 5], [11, 6], [-4, 6]], 'armor', { sh: 1.8, hl: 0.9, r: 0.5 });
    d.plate([[5.8, 1.4], [11, 5], [11, 6], [5.8, 6]], 'accent', { sh: 0.8, hl: 0.6, ink: 0.5 });
    d.plate([[-2.5, 1.5], [-5.4, 6], [-1.4, 6]], 'frame', { sh: 0.6, hl: 0.4, ink: 0.6 });
    d.plate([[-4.5, 4.8], [11, 4.8], [11, 6], [-4.5, 6]], 'frame', { sh: 0, hl: 0, ink: 0.5 });
  },
  pelvis(d) {
    d.plate([[-5, -3], [5, -3], [5.5, 3], [2, 6.5], [-3.5, 6.5], [-6, 2]], 'frame', { sh: 1.6 });
    d.plate([[-8, -2.5], [-3.5, -3.5], [-3, 3.5], [-6, 9], [-8.5, 4]], 'plate', { sh: 1.4, hl: 0.8 });
  },
  hipPlate(d) {
    d.plate([[-2, -4], [6, -4.5], [8.2, 1], [5, 9], [1, 6.5], [-1.5, 1]], 'armor', { sh: 2, hl: 1 });
    d.edge([[-1.6, -3.8], [5.8, -4.3]]);
    d.strip([[5.6, 1], [3.8, 6]], 1, 0.8);
  },
  backpack(d) {
    d.plate([[-6.5, -12], [-10.5, -15], [-11.6, -26], [-8.5, -30], [-5.5, -28]], 'plate', { sh: 1.8, hl: 0.8 });
    d.strip([[-10.4, -17], [-11, -24.5]], 1, 0.8);
  },
  torso(d, fx) {
    d.plate([[-3.5, -1], [3, -1], [3.6, -11.5], [-3.6, -11.5]], 'frame', { sh: 1.4 });
    d.panel([[-3.4, -4.5], [3.2, -4.5]]);
    d.panel([[-3.5, -8], [3.4, -8]]);
    d.plate([[-7.5, -10.5], [2.5, -11.5], [3.2, -14.2], [-7, -14]], 'plate', { sh: 1, hl: 0.6, ink: 0.7 });
    d.plate([[-8.2, -13], [3.5, -13.5], [8.4, -20], [7.4, -26.5], [3, -30.5], [-5, -31.2], [-8.6, -25]], 'armor', { sh: 3.6, hl: 1.2, r: 0.7 });
    d.edge([[-8.3, -25], [-5, -30.9], [2.9, -30.2]]);
    d.panel([[-8.4, -20], [-1.5, -20.5]]);
    d.plate([[-6, -28.6], [2.6, -28.8], [5.8, -25.6], [-6.8, -26]], 'accent', { sh: 0.8, hl: 0.6, ink: 0.6 });
    // Open chest ring with the floating core.
    d.disc(5, -19.5, 4.8, 'frame', { sh: 1.2, hl: 0.8 });
    d.lampDisc(5, -19.5 + fx.bob * 0.25, 2.5, 1 + 0.25 * fx.flare);
  },
  head(d) {
    d.plate([[-1.8, 1.5], [1.8, 1.5], [1.5, -5], [-1.5, -5]], 'frame', { sh: 0.8 });
    d.plate([[-5.2, -10], [-11.5, -14], [-9, -10.6], [-6, -7]], 'accent', { sh: 0.8, hl: 0.6 });
    d.plate([[-5, -4], [-6, -11], [-2.5, -15.5], [3, -15], [7.5, -11], [10.2, -6.4], [6, -3], [-1, -2.5]], 'armor', { sh: 2.4, hl: 1.1, r: 0.7 });
    d.edge([[-5.9, -11], [-2.5, -15.2], [2.9, -14.7]]);
    d.plate([[2.2, -11.6], [7.4, -10.6], [10.2, -6.2], [6.2, -3.4], [2.6, -8]], 'frame', { sh: 0.6, hl: 0.5, ink: 0.6 });
    d.lampPoly([[3, -10.4], [9.6, -6.9], [9, -5.8], [3, -9]], 1, 0.4);
  },
  behind(d, style, sk, fx) {
    wraithFin(d, style, sk, fx, 'R');
  },
  behindHead(d, style, sk, fx) {
    wraithHalo(d, style, sk, fx);
  },
  front(d, style, sk, fx) {
    wraithFin(d, style, sk, fx, 'F');
  },
};

/** Floating shoulder fin. Hovers above-behind its shoulder, flares outward with the signature, docks on death. */
function wraithFin(d: Drawer, style: MechStyle, sk: Skeleton, fx: FrameFx, side: Side): void {
  const t = style.torso;
  const sh = side === 'F' ? t.shoulderF : t.shoulderR;
  const dock = fx.dock;
  const flare = fx.flare;
  const bob = (side === 'F' ? fx.bob : -fx.bob * 0.8) * (1 - dock);
  const lift = (8.5 + 4 * flare) * (1 - dock) + 3.5 * dock;
  const back = (side === 'F' ? 2.5 : 4.5) + 4 * flare;
  const [x, y] = apply(sk.torso, [sh[0] - back * (1 - dock * 0.7), sh[1] - lift + bob]);
  const rot = sk.torso.rot + (side === 'F' ? -0.42 : -0.62) - 0.55 * flare + 0.35 * dock;
  const prevDepth = d.depth;
  if (side === 'R') d.depth = 0.75;
  d.push(x, y, rot);
  d.plate([[-3, 1.5], [1, -2.5], [17, -7.5], [21, -6], [5, 3.5]], 'armor', { sh: 1.8, hl: 0.9, r: 0.5 });
  d.edge([[1.2, -2.2], [16.8, -7.2]]);
  d.plate([[3, 0], [12, -3.6], [11, -1.6], [4, 1.5]], 'accent', { sh: 0.6, hl: 0.5, ink: 0.5 });
  d.strip([[5, 2.6], [19.5, -5.2]], 1, (0.9 + 0.6 * flare) * (1 - 0.85 * dock));
  d.pop();
  d.depth = prevDepth;
}

/** Broken halo hovering behind the head. */
function wraithHalo(d: Drawer, style: MechStyle, sk: Skeleton, fx: FrameFx): void {
  const dock = fx.dock;
  const flare = fx.flare;
  const [x, y] = apply(sk.torso, [style.torso.neck[0] - 7 - 2 * flare, style.torso.neck[1] - 12 - 3 * flare + fx.bob * 0.6 * (1 - dock) + 9 * dock]);
  const r = (7.5 + 3.5 * flare) * (1 - 0.35 * dock);
  const spin = 0.5 + 1.2 * flare + fx.t * 0.3 * flare;
  d.push(x, y, sk.torso.rot);
  d.g.save();
  for (let i = 0; i < 3; i++) {
    const a0 = spin + (i * Math.PI * 2) / 3;
    const a1 = a0 + 1.45;
    const seg = new Path2D();
    seg.arc(0, 0, r + 1.6, a0, a1);
    seg.arc(0, 0, r - 1.6, a1, a0, true);
    seg.closePath();
    d.shape(seg, 'plate', { sh: 1, hl: 0.7 });
    const arc: Pt[] = [];
    for (let k = 0; k <= 6; k++) {
      const a = a0 + ((a1 - a0) * k) / 6;
      arc.push([Math.cos(a) * (r - 0.6), Math.sin(a) * (r - 0.6)]);
    }
    d.strip(arc, 0.9, (0.85 + 0.6 * flare) * (1 - 0.9 * dock));
  }
  d.g.restore();
  d.pop();
}

/** BASTION: everything is in the painting. */
const BASTION = withCutout('bastion', BASTION_VECTOR);
/** WRAITH: the painting has the floating shoulder plates and the blade-fin halo, so the vector fins/halo are dropped. */
const WRAITH = withCutout('wraith', WRAITH_VECTOR);

export const PART_LIBS: Readonly<Record<string, PartLib>> = { volt: VOLT, bastion: BASTION, wraith: WRAITH };

// ───────────────────────────── Shared helpers ─────────────────────────────

/** Small deterministic PRNG for spark/arc placement (art must be identical every boot). */
export function seeded(seed: number): () => number {
  let s = seed >>> 0 || 1;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Jagged lightning polyline from a to b. */
export function jagged(a: Pt, b: Pt, steps: number, amp: number, rnd: () => number): Pt[] {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  const pts: Pt[] = [a];
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    const o = (rnd() * 2 - 1) * amp;
    pts.push([a[0] + dx * t + nx * o, a[1] + dy * t + ny * o]);
  }
  pts.push(b);
  return pts;
}
